import React, { useLayoutEffect, useState } from "react";
import { cn } from "../classnames.js";
import { ClipboardButton } from "../ClipboardButton/ClipboardButton.js";
import { highlight, isSupportedLanguage, type Language } from "./highlight.js";

const Code = ({
  children,
  className, // eslint-disable-line react/prop-types
  copiable,
  ...otherProps
}: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
  copiable: boolean;
}) => (
  <div className={cn("tw-overflow-x-auto")}>
    <code
      className={cn(
        "tw-flex",
        "tw-flex-col",
        // Required to make right padding apply when scrolling
        "tw-float-left",

        "tw-py-6",
        "tw-pl-4",
        // When the copy button is displayed, add some right padding to extend the scroll distance.
        // This makes content that would otherwise be rendered underneath the button readable.
        copiable ? "tw-pr-20" : "tw-pr-4",

        // Disable line wrapping on long lines (we want long lines to trigger scrolling)
        "tw-whitespace-pre",
        className,
      )}
      {...otherProps}
    >
      {children}
    </code>
  </div>
);

const Pre = ({
  // Ignore the style prop, which the highlighter uses to set the background color. (In order to
  // match the Netlify app's highlighter styling, we instead control the background color manually.)
  style: _style, // eslint-disable-line react/prop-types

  children,
  className, // eslint-disable-line react/prop-types
  copiable,
  copyValue,
  ...otherProps
}: React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLPreElement>,
  HTMLPreElement
> & { copiable: boolean; copyValue: string }) => (
  <pre
    className={cn("tw-bg-black", "tw-p-0", className)}
    {...otherProps}
    data-testid="codeblock-highlighted"
  >
    {children}
    {copiable && (
      <div
        className={cn(
          "tw-absolute",

          // Vertically center the button and pin it to the top right corner
          "tw-top-8",
          "tw-right-8",
          "tw-height-100%",

          // Add a drop shadow around the button
          "tw-shadow-[8px_0_0_8px_theme('colors.black.DEFAULT'),_-8px_1px_12px_12px_theme('colors.black.DEFAULT')]",
          "dark:tw-shadow-[8px_0_0_8px_theme('colors.black.darker'),_-8px_1px_12px_12px_theme('colors.black.darker')]",
        )}
      >
        <ClipboardButton value={copyValue} />
      </div>
    )}
  </pre>
);

export type CodeBlockProps = {
  /**
   * Your code (as text).
   */
  content: string;

  /**
   * Whether or not to apply syntax highlighting to the rendered code.
   *
   * @default true
   */
  highlighting?: boolean | undefined;

  /**
   * Whether or not to show a clipboard button which, when clicked, copies the current snippet to
   * the user clipboard.
   *
   * @default false
   */
  copiable?: boolean | undefined;

  /**
   * Specify the code language to apply syntax highlighting to the code block.
   *
   * @default text
   */
  language?: Language | undefined;
};

/**
 * A code block renders code in a pre-formatted monospace text block with syntax highlighting.
 *
 * ![Code block with syntax highlighting](./screenshots/code_block_with_highlighting.png)
 *
 * @param props
 * @example
 * ```tsx
 * import { CodeBlock } from "@netlify/sdk/ui/react/components";
 *
 * <CodeBlock content={`<p>Hello, world!</p>`} language="html" />
 * ```
 * @example
 * ```tsx
 * import { CodeBlock } from "@netlify/sdk/ui/react/components";
 *
 * <CodeBlock content={`<p>Hello, world!</p>`} />
 * ```
 */
export const CodeBlock = ({
  content,
  highlighting = true,
  copiable = false,
  language = "text",
}: CodeBlockProps) => {
  const [nodes, setNodes] = useState<React.JSX.Element | null>(null);

  if (!isSupportedLanguage(language)) {
    language = "text";
  }

  useLayoutEffect(() => {
    if (highlighting) {
      void (async () => {
        setNodes(
          await highlight(content, {
            components: {
              code: ({ children, className, ...otherProps }) => (
                <Code copiable={copiable} {...otherProps}>
                  {children}
                </Code>
              ),
              pre: ({ children, className, ...otherProps }) => (
                <Pre copiable={copiable} copyValue={content} {...otherProps}>
                  {children}
                </Pre>
              ),
            },
            language,
            theme: "github-dark-default",
          }),
        );
      })();
    }
  }, [content, highlighting, language, copiable]);

  // Code highlighting is asynchronous. It will usually complete before a user can even notice it,
  // but show un-highlighted code while the highlighter is working.
  return (
    nodes ?? (
      <Pre className="tw-bg-black" copiable={copiable} copyValue={content}>
        <Code className="tw-text-white" copiable={copiable}>
          {content}
        </Code>
      </Pre>
    )
  );
};
